import { c as _c } from "react/compiler-runtime";
import type { JSONContent } from '@tiptap/react';
import { cx } from 'class-variance-authority';
import { useReducedMotion } from 'framer-motion';
import { useRouter } from 'next/router';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatInput, type ChatInputHandle, ComposerDisclaimer, composerActions, type ResourceContext } from '@/components/agent-chat';
import useMobileLayout from '@/hooks/use-mobile-layout';
import ActionPills from './components/action-pills';
import BlockedComposer from './components/blocked-composer';
import HomeBackdrop from './components/home-backdrop';
import PercyHero from './components/percy-hero';
import SuggestionList from './components/suggestion-list';
import type { TemplateBackedAgentId } from './data/agent-templates';
import { type AskPercyVariant, usePills } from './hooks/use-pills';
import useSuggestions from './hooks/use-suggestions';
type AskPercyLabels = {
  title: string;
  subtitle: string;
  inputPlaceholder: string;
  loadingText: string;
  dropOverlay: string;
};
type AskPercySubmit = {
  query: string;
  files: File[];
  env?: string;
  restrictToSandbox: boolean;
  /** Route-derived resource the chat is about, when composing on a resource page with the chip active. */
  context?: ResourceContext;
  /** Resources the user @-mentioned in the composer; carried to the first message so it renders names. */
  mentions?: ResourceContext[];
};
type AskPercyProps = {
  labels: AskPercyLabels;
  isFirmEmployee?: boolean;
  /** Whether the tenant can flip between sandbox and live data. */
  canRestrictToSandbox?: boolean;
  /** Locks the env selector, for chats about a resource that exists in one env only. */
  pinnedEnv?: {
    value: 'sandbox' | 'production';
    tooltip: string;
  };
  /** Optional initial text to seed the prompt editor with — useful for tests/storybook. */
  defaultValue?: string;
  isLoading: boolean;
  canStartChat?: boolean;
  onSubmit: (params: AskPercySubmit) => void | Promise<void>;
  renderHeader?: React.ReactNode;
  variant?: AskPercyVariant;
  /** Centered on full pages; pinned to the bottom in sidebar and floating views. */
  placement?: 'centered' | 'bottom';
  /** The Percy home: greets by name and cycles its headline through the suggestions. */
  home?: {
    firstName?: string;
  };
};
type CenteredLayoutProps = {
  renderHeader?: React.ReactNode;
  hero: React.ReactNode;
  composer: React.ReactNode;
  suggestions: React.ReactNode;
};
const CenteredLayout = t0 => {
  const $ = _c(15);
  const {
    renderHeader,
    hero,
    composer,
    suggestions
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <HomeBackdrop />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== renderHeader) {
    t2 = renderHeader && <div className="absolute top-6 left-6 z-10">{renderHeader}</div>;
    $[1] = renderHeader;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div aria-hidden={true} className="pointer-events-none absolute inset-x-10 -bottom-3 top-6 rounded-full bg-accent opacity-0 blur-2xl transition-opacity duration-500 group-focus-within/composer:opacity-[var(--percy-composer-glow,0.14)]" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== composer) {
    t4 = <div className="group/composer relative w-full max-w-[680px]">{t3}{composer}</div>;
    $[4] = composer;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== suggestions || $[7] !== t4) {
    t5 = <div className="flex w-full flex-col items-center gap-5">{t4}{suggestions}</div>;
    $[6] = suggestions;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== hero || $[10] !== t5) {
    t6 = <div className="relative flex min-h-full flex-col items-center justify-center px-4 py-16 group-data-[keyboard-open]/app:pb-4 md:px-8"><div className="flex w-full flex-col items-center gap-3 md:gap-7">{hero}{t5}</div></div>;
    $[9] = hero;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t2 || $[13] !== t6) {
    t7 = <div className="absolute inset-0 overflow-x-hidden overflow-y-auto">{t1}{t2}{t6}</div>;
    $[12] = t2;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
};
const AskPercy = ({
  labels,
  isFirmEmployee = false,
  canRestrictToSandbox = true,
  pinnedEnv,
  defaultValue,
  isLoading,
  canStartChat = true,
  onSubmit,
  renderHeader,
  variant,
  placement = 'centered',
  home
}: AskPercyProps) => {
  const {
    t
  } = useTranslation('ask-percy');
  const {
    t: tForm
  } = useTranslation('ask-percy', {
    keyPrefix: 'form'
  });
  // Fullscreen vs floating/sidebar render the composer in different JSX subtrees, so switching
  // layout remounts ChatInput and would drop an unsent draft. AskPercy itself doesn't remount,
  // so hold the draft here and re-seed the fresh editor via `initialContent`.
  const draftRef = useRef<JSONContent | undefined>(undefined);
  const [previewPrompt, setPreviewPrompt] = useState<string | null>(null);
  const [seedText, setSeedText] = useState<string | undefined>();
  const [seedKey, setSeedKey] = useState(0);
  const [env, setEnv] = useState('');
  const [restrictToSandbox, setRestrictToSandbox] = useState(false);
  const reduceMotion = useReducedMotion() ?? false;
  const isMobile = useMobileLayout();
  // A tenant that can't toggle is sandbox-restricted, so it can only create
  // sandbox chats — force sandbox regardless of the state above or `pinnedEnv`,
  // otherwise we'd submit is_live=true and the backend would reject it.
  const lockedEnv = canRestrictToSandbox ? pinnedEnv?.value : 'sandbox';
  const effectiveRestrictToSandbox = lockedEnv ? lockedEnv === 'sandbox' : restrictToSandbox;
  const sandboxToggleValue = effectiveRestrictToSandbox ? 'sandbox' : 'production';
  const disabled = isLoading || !canStartChat;
  // Distinct from `disabled`: the blocking overlay must not appear during the transient loading state.
  const blocked = !canStartChat;
  const router = useRouter();
  // Picking a suggestion fills the composer rather than sending it. The prompt only ever appears
  // in the placeholder, so a click that sends spends a run on wording nobody read. A `{|}` marker
  // still places the caret.
  const seedInput = (prompt: string, options?: {
    sandbox?: boolean;
  }) => {
    setSeedText(prompt);
    setSeedKey(k => k + 1);
    if (options?.sandbox) setRestrictToSandbox(true);
  };
  const inputRef = useRef<ChatInputHandle>(null);

  // Backed templates reuse the /agents dropdown's bootstrap (deterministic, no Percy turn);
  // other pills fall through to seeding a Percy prompt in use-pills.
  const createFromTemplate = (template: TemplateBackedAgentId) => {
    if (!router.isReady) return;
    router.push(`/agents/builder?template=${template}`);
  };
  const pills = usePills(variant, seedInput, createFromTemplate);
  const isBottom = placement === 'bottom';
  const actions = composerActions({
    env: {
      value: sandboxToggleValue,
      onChange: lockedEnv ? undefined : next => setRestrictToSandbox(next === 'sandbox'),
      lockedTooltip: canRestrictToSandbox ? pinnedEnv?.tooltip : tForm($ => $.sandboxLocked)
    },
    percyEnv: isFirmEmployee ? {
      value: env,
      onChange: setEnv
    } : undefined
  });
  const {
    suggestions,
    headline,
    hoveredPrompt,
    setHoveredId,
    select,
    holdHeadline,
    more
  } = useSuggestions({
    variant,
    pills,
    listed: !isBottom,
    showAll: isMobile,
    cycleHeadline: !!home && !isBottom && !blocked && !reduceMotion,
    seedInput
  });
  const header = <h1 className="pl-2 text-heading-5 text-primary">{labels.title}</h1>;
  const actionPills = isBottom && pills && <div className={cx('w-full', disabled && 'opacity-50')}>
      <ActionPills pills={pills} layout="list" onPreviewPromptChange={setPreviewPrompt} disabled={blocked} />
    </div>;
  const chatInput =
  // Bottom placement matches ChatPanel's input band (px-3, max-w-[820px]) so the composer
  // doesn't shift when the empty state swaps for the live chat. Centered page keeps its own.
  <div className={cx('relative mx-auto w-full', isBottom ? 'px-3 max-w-[820px]' : 'max-w-[680px]')}>
      <ChatInput ref={inputRef} placeholder={canStartChat ? hoveredPrompt || previewPrompt || labels.inputPlaceholder : t($_0 => $_0.noPermission)} rows={isBottom ? 2 : 3} initialContent={draftRef.current ?? defaultValue} onChange={({
      isEmpty,
      content
    }) => {
      draftRef.current = content;
      if (!isEmpty) holdHeadline();
    }} seedText={seedText} seedKey={seedKey} flashOnSeed={isBottom} disabled={disabled} isLive={!effectiveRestrictToSandbox} isLoading={isLoading} loadingText={labels.loadingText} onSendMessage={async (content_0, {
      files,
      context,
      mentions
    }) => await onSubmit({
      query: content_0,
      files: files ?? [],
      env: env.trim() || undefined,
      restrictToSandbox: effectiveRestrictToSandbox,
      context,
      mentions
    })} {...actions} />
    </div>;

  // BlockedComposer lays the docked panel's ghost rows out itself, outside the greeting's column;
  // they need that column back or they hug the panel's left edge.
  const composerBlock = blocked ? <BlockedComposer pills={isBottom && <div className="mx-auto w-full max-w-[820px] px-3">{actionPills}</div>} input={chatInput} message={t($_1 => $_1.noPermission)} /> : chatInput;
  if (isBottom) {
    // Greeting + pills centered, input pinned to the bottom. The disclaimer
    // flows below the input so a two-line wrap grows the stack instead of
    // overlapping it.
    return <div className="flex flex-col items-center pb-4 absolute inset-0">
        {renderHeader && <div className="absolute top-6 left-6">{renderHeader}</div>}
        {/* Suggestions sit with the greeting they answer rather than stacked on the composer, which
            keeps the composer a single object. Docked panel only — the centred landing page has room
            for both. */}
        <div className="flex-1 min-h-0 w-full flex flex-col justify-end px-3 pb-4">
          <div className="mx-auto w-full max-w-[820px] flex flex-col gap-3">
            {header}
            {!blocked && actionPills}
          </div>
        </div>
        <div className="w-full flex flex-col items-center">
          {composerBlock}
          <ComposerDisclaimer />
        </div>
      </div>;
  }
  return <CenteredLayout renderHeader={renderHeader} hero={<PercyHero title={labels.title} subtitle={home ? undefined : labels.subtitle} greeting={home && {
    name: home.firstName,
    headline
  }} />} composer={composerBlock} suggestions={<SuggestionList suggestions={suggestions} disabled={disabled} onSelect={select} onHoverChange={setHoveredId} more={more} />} />;
};
export type { AskPercyLabels, AskPercyProps, AskPercySubmit, AskPercyVariant };
export default AskPercy;